import type { User, HouseholdMember, HouseholdMemberRelation, FiscalData, TenantSelection, MagicLinkVerifyResponse, CreateHouseholdMemberRequest, ImpersonateUserResponse } from '@yawi/types';
import type { PaginatedResponse, QueryParams } from '../types';

/** Login no puede completarse: el usuario pertenece a varios tenants y debe elegir uno (POST /auth/select-tenant). */
export class TenantSelectionRequiredError extends Error {
  constructor(
    public readonly userId: string,
    public readonly tenants: TenantSelection[],
  ) {
    super('TENANT_SELECTION_REQUIRED');
    this.name = 'TenantSelectionRequiredError';
  }
}

export interface ConvertResidentToHouseholdMemberResult {
  user: User;
  householdMember: HouseholdMember;
}

export interface ConvertResidentToHouseholdMemberInput {
  assignToResidentId: string;
  relation: HouseholdMemberRelation;
  age?: number;
}

export interface ConvertHouseholdMemberToResidentInput {
  isPrimary?: boolean;
  isOwner?: boolean;
}

export interface ConvertHouseholdMemberToResidentResult {
  user: User;
}

export type CreateUnitHouseholdMemberInput = Omit<HouseholdMember, 'id' | 'accountStatus'>;

export interface IUserRepository {
  getCurrentUser(): Promise<User>;
  /**
   * Lanza TenantSelectionRequiredError si el correo resuelve a más de una cuenta en distintos tenants.
   * `tenantId` resuelve el login en un solo paso contra la cuenta de ese tenant (ver getMyAccounts).
   */
  login(email: string, pin: string, tenantId?: string): Promise<User>;
  updatePin(userId: string, newPin: string): Promise<void>;
  getAllUsers(): Promise<User[]>;
  getUsers(params?: QueryParams): Promise<PaginatedResponse<User>>;
  createUser(user: Omit<User, 'id'>): Promise<User>;
  updateUser(user: User): Promise<User>;
  deleteUser(userId: string): Promise<void>;
  restoreUser(userId: string): Promise<User>;
  /** Bloquea al usuario y revoca sus tokens (PATCH /users/:id/block). */
  blockUser(userId: string): Promise<User>;
  /** Desbloquea al usuario y reactiva su membresía en el tenant (PATCH /users/:id/unblock). */
  unblockUser(userId: string): Promise<User>;
  logout(): Promise<void>;

  // Profile methods
  updateProfile(updates: Partial<User>): Promise<User>;
  changePassword(currentPassword: string, newPassword: string): Promise<void>;

  // Household members
  getHouseholdMembers(): Promise<HouseholdMember[]>;
  createHouseholdMember(member: Omit<HouseholdMember, 'id' | 'accountStatus'>): Promise<HouseholdMember>;
  updateHouseholdMember(memberId: string, member: Partial<HouseholdMember>): Promise<HouseholdMember>;
  deleteHouseholdMember(memberId: string): Promise<void>;
  resendHouseholdMemberInvitation(memberId: string): Promise<void>;
  resendUnitHouseholdMemberInvitation(unitId: string, memberId: string): Promise<void>;
  getUserHouseholdMembers(userId: string): Promise<HouseholdMember[]>;
  createUserHouseholdMember(userId: string, member: Omit<HouseholdMember, 'id' | 'accountStatus'>): Promise<HouseholdMember>;
  getUnitHouseholdMembers(unitId: string): Promise<HouseholdMember[]>;
  createUnitHouseholdMember(unitId: string, residentId: string, member: CreateUnitHouseholdMemberInput): Promise<HouseholdMember>;
  deleteUnitHouseholdMember(unitId: string, memberId: string): Promise<void>;
  /** Asigna/transfiere el residente principal de la unidad; el backend degrada al principal anterior. */
  transferUnitPrincipal(unitId: string, targetMemberId: string): Promise<void>;
  convertResidentToHouseholdMember(
    unitId: string,
    residentId: string,
    input: ConvertResidentToHouseholdMemberInput,
  ): Promise<ConvertResidentToHouseholdMemberResult>;
  convertHouseholdMemberToResident(
    unitId: string,
    memberId: string,
    input?: ConvertHouseholdMemberToResidentInput,
  ): Promise<ConvertHouseholdMemberToResidentResult>;

  // Fiscal data
  getFiscalData(): Promise<FiscalData | null>;
  upsertFiscalData(fiscalData: FiscalData): Promise<FiscalData>;
  deleteFiscalData(): Promise<void>;

  // Administrative
  getUserById(id: string): Promise<User | null>;
  blockUser(userId: string): Promise<User>;
  unblockUser(userId: string): Promise<User>;
  /** ADMIN previewing a member of their own tenant with that member's real tokens/permissions. */
  impersonateMember(memberId: string): Promise<ImpersonateUserResponse>;

  // Token management
  refreshToken(): Promise<void>;

  // Multi-tenant
  // GET /auth/my-tenants devuelve membresías (tenantId/tenantName/tenantSlug/role), no Tenant crudo.
  getMyTenants(): Promise<TenantSelection[]>;
  /**
   * Tenants de TODAS las cuentas que comparten el correo de la sesión, no solo
   * las membresías del MasterUser actual: dar de alta un tenant crea una cuenta
   * nueva por tenant, así que getMyTenants devuelve una sola. Entrar a otro
   * tenant exige su contraseña — login(email, pin, tenantId).
   */
  getMyAccounts(): Promise<TenantSelection[]>;
  /** userId es requerido: el endpoint select-tenant es público y no puede resolverlo de un JWT. El repo reenvía el selectionToken del último login. */
  selectTenant(userId: string, tenantId: string): Promise<User>;

  // Magic Link authentication
  verifyMagicLink(token: string): Promise<MagicLinkVerifyResponse>;
  requestMagicLink(email: string): Promise<void>;
  selectTenantForMagicLink(userId: string, tenantId: string): Promise<MagicLinkVerifyResponse>;

  // Password recovery
  forgotPassword(email: string): Promise<void>;
  resetPassword(token: string, newPassword: string): Promise<void>;

  /**
   * Asigna la primera contraseña propia a una cuenta invitada por magic link
   * (nunca supo la contraseña aleatoria que el backend le generó). Falla si
   * la cuenta ya tiene una contraseña asignada (usar changePassword en ese caso).
   */
  setInitialPassword(newPassword: string): Promise<void>;
}
